// Tenant select and create.
import { useState, type FormEvent } from "react";
import { ApiError } from "../api/client";
import { useServices } from "../components/context";
import { ErrorBanner, Section, useAction } from "../components/ui";
import { useLoad } from "../components/useLoad";
import { tenantLimitText } from "../lib/summary";
import { slugError } from "../lib/format";
import { routeHash } from "../lib/router";

/** Shown in place of the create form while tenant sign-up is closed (Q20). */
export const SIGNUP_CLOSED_TEXT = "Sign-up is not open yet. Join the waitlist at prego.k8skill.com.";

function isSignupClosed(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.code === "signup_closed";
}

export function Tenants() {
  const { api } = useServices();
  const me = useLoad(() => api.me(), []);
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [consumer, setConsumer] = useState(false);
  const create = useAction();
  const slugMsg = slug ? slugError(slug, true) : null;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (slugError(slug, true)) return;
    const ok = await create.run(async () => {
      const body = consumer ? { slug, name, plan: "consumer" as const } : { slug, name };
      const t = await api.createTenant(body);
      window.location.hash = routeHash({ name: "tenant", tenant: t.slug, page: "overview" });
    });
    if (ok) {
      setSlug("");
      setName("");
    }
  }

  const memberships = me.data?.memberships ?? [];
  const signupClosed = me.data?.signup === "closed" || isSignupClosed(create.error);
  // Shown only while sign-up is closed (registry 0.14.0 `tenant_limit`).
  const limitText = signupClosed ? tenantLimitText(me.data?.tenant_limit) : null;

  return (
    <>
      <Section title="Your tenants">
        <ErrorBanner error={me.error} />
        {me.loading && !me.data ? (
          <p className="muted">Loading...</p>
        ) : memberships.length === 0 ? (
          <p className="muted">
            {signupClosed
              ? "You are not a member of a tenant yet. Ask an admin to add you."
              : "You are not a member of a tenant yet. Create one below, or ask an admin to add you."}
          </p>
        ) : (
          <ul className="list">
            {memberships.map((m) => (
              <li key={m.tenant_id}>
                <a href={routeHash({ name: "tenant", tenant: m.tenant_slug, page: "overview" })}>
                  <strong>{m.tenant_name}</strong> <span className="mono">{m.tenant_slug}</span>
                </a>
                <span className="tag">{m.role}</span>
              </li>
            ))}
          </ul>
        )}
        {(me.data?.blocked_invites ?? []).map((b) => (
          <p key={b.tenant_slug} className="banner warn" role="status">
            Your invite to {b.tenant_slug} as {b.role} waits: {b.reason}
          </p>
        ))}
        {limitText && (
          <p className="small muted" data-testid="tenant-limit">
            {limitText}
          </p>
        )}
      </Section>
      <Section title="Create a tenant">
        {signupClosed ? (
          <p className="banner" role="status">
            {SIGNUP_CLOSED_TEXT}
          </p>
        ) : (
          <form onSubmit={onSubmit} className="form">
            <label>
              Name
              <input
                required
                maxLength={100}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Acme Inc."
              />
            </label>
            <label>
              Slug
              <input
                required
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                placeholder="acme"
                aria-invalid={slugMsg ? true : undefined}
              />
              <small className={slugMsg ? "error" : "muted"}>
                {slugMsg ?? "Used in registry URLs. You cannot change it later."}
              </small>
            </label>
            <label className="check">
              <input type="checkbox" checked={consumer} onChange={(e) => setConsumer(e.target.checked)} />
              This tenant only pulls crates that other tenants share (consumer plan, no publish).
            </label>
            <p className="muted">A new tenant starts a 30-day trial, unless it is a consumer tenant.</p>
            <button type="submit" disabled={create.busy || !!slugMsg}>
              Create tenant
            </button>
            <ErrorBanner error={create.error} />
          </form>
        )}
      </Section>
    </>
  );
}
